/**
 * Device cache of recent InsideChat photos and voice clips.
 * IndexedDB blobs keyed by "source:id"; keep the newest N only.
 */

const DB_NAME = 'InsideChatMedia';
const STORE_NAME = 'mediaBlobs';
const DB_VERSION = 1;
export const CHAT_MEDIA_CACHE_LIMIT = 20;

function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
  });
}

function idbRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function waitForTx(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/** @returns {Promise<Blob|undefined>} */
export async function getCachedChatMedia(key) {
  if (!key) return undefined;
  let db;
  try {
    db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const row = await idbRequest(tx.objectStore(STORE_NAME).get(key));
    await waitForTx(tx);
    if (!row || !(row.blob instanceof Blob) || row.blob.size === 0) {
      return undefined;
    }
    return row.blob;
  } catch (err) {
    console.error('Error reading chat media cache:', err);
    return undefined;
  } finally {
    if (db) db.close();
  }
}

export async function putCachedChatMedia(key, blob) {
  if (!key || !(blob instanceof Blob) || blob.size === 0) return;
  let db;
  try {
    db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put({
      id: key,
      blob,
      mimeType: blob.type || '',
      cachedAt: Date.now(),
    });
    await waitForTx(tx);
  } catch (err) {
    console.error('Error writing chat media cache:', err);
  } finally {
    if (db) db.close();
  }
}

/** Drop any cached rows whose key is not in keepKeys. */
export async function pruneCachedChatMedia(keepKeys) {
  const keep = new Set((keepKeys || []).filter(Boolean));
  let db;
  try {
    db = await openDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const keys = await idbRequest(store.getAllKeys());
    (keys || []).forEach((id) => {
      if (!keep.has(id)) store.delete(id);
    });
    await waitForTx(tx);
  } catch (err) {
    console.error('Error pruning chat media cache:', err);
  } finally {
    if (db) db.close();
  }
}
